import React, { useRef, useState } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import api, { errText } from '../lib/api';
import { useToast } from '../hooks/use-toast';

// Records short audio in-browser (MediaRecorder), transcribes via Whisper.
// Props:
//  - onTranscript(text)  : called with the transcribed text
//  - onAudio({url, blob}) : optional; when set, the recording is uploaded and the url returned
//  - label               : optional button label (default "Record")
export const VoiceRecorder = ({ onTranscript, onAudio, label = 'Record', testid = 'voice-recorder' }) => {
  const { toast } = useToast();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast({ title: 'Microphone blocked', description: 'Please allow microphone access to record.' });
    }
  };

  const stop = () => {
    try { mediaRef.current && mediaRef.current.stop(); } catch { /* noop */ }
    setRecording(false);
  };

  const handleStop = async () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
    if (!blob.size) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'recording.webm');
      const r = await api.post('/assistant/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      const text = r.data?.text || '';
      if (text) onTranscript?.(text);
      else toast({ title: 'No speech detected', description: 'Please try recording again.' });

      if (onAudio) {
        const fd2 = new FormData();
        fd2.append('file', blob, 'recording.webm');
        const up = await api.post('/assistant/upload-media', fd2, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
        onAudio({ url: up.data?.url, blob });
      }
    } catch (e) {
      toast({ title: 'Transcription failed', description: errText(e) });
    } finally {
      setBusy(false);
    }
  };

  if (busy) {
    return (
      <span className="inline-flex items-center gap-2 text-sm text-mahogany/70" data-testid={`${testid}-busy`}>
        <Loader2 className="w-4 h-4 animate-spin" /> Transcribing…
      </span>
    );
  }

  return recording ? (
    <button type="button" onClick={stop} data-testid={`${testid}-stop`}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-red-600 text-white animate-pulse">
      <Square className="w-4 h-4" /> Stop &amp; transcribe
    </button>
  ) : (
    <button type="button" onClick={start} data-testid={`${testid}-start`}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm border border-rose/50 bg-white/70 text-burgundy hover:bg-rose-soft/50 transition-colors">
      <Mic className="w-4 h-4" /> {label}
    </button>
  );
};

export default VoiceRecorder;
